import { useParams, Link } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { track } from '../lib/track'
import { getProduct, getCategory, calcCommission, calcDomainCommission, COMMISSION, demoHref } from '../lib/products'
import { api, type LeadInput } from '../lib/api'
import type { User } from '../lib/auth'
import { useI18n } from '../lib/i18n'

export default function ProductDetail({ user }: { user: User | null }) {
  const { t, lang } = useI18n()
  const { slug } = useParams<{ slug: string }>()
  useEffect(() => {
    if (slug) track('product_view', { product: slug })
  }, [slug])
  const product = slug ? getProduct(slug) : null
  const [showForm, setShowForm] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  if (!product) {
    return (
      <section className="section py-20 text-center">
        <h1 className="text-3xl font-bold text-white">{t('Product not found')}</h1>
        <Link to="/catalog" className="btn-secondary mt-6 inline-flex">{t('Back to catalog')}</Link>
      </section>
    )
  }

  const cat = getCategory(product.category)
  const comm = {
    starter: calcCommission(product.plans.starter.price),
    pro: calcCommission(product.plans.pro.price),
    business: calcCommission(product.plans.business.price),
  }

  return (
    <section className="section py-10">
      <nav className="text-sm text-ink-500 mb-6">
        <Link to="/catalog" className="hover:text-brand-500">{t('Catalog')}</Link>
        {' / '}
        <Link to={`/catalog?cat=${product.category}`} className="hover:text-brand-500">
          {cat ? t(cat.name) : null}
        </Link>
        {' / '}
        <span className="text-ink-100">{product.name}</span>
      </nav>

      {/* Header */}
      <header className="flex flex-col sm:flex-row gap-6 mb-10">
        <div className={`shrink-0 w-20 h-20 rounded-2xl bg-gradient-to-br ${cat?.color ?? 'from-brand-500 to-brand-700'} grid place-items-center text-4xl`}>
          {product.icon}
        </div>
        <div className="flex-1">
          <h1 className="text-3xl sm:text-4xl font-bold text-white">{product.name}</h1>
          <p className="text-lg text-ink-100 mt-2">{t(product.tagline)}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {product.demoUrl && (
              <a
                href={demoHref(product, lang)}
                onClick={() => track('demo_click', { product: product.slug })}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
              >
                {t('Try live demo ↗')}
              </a>
            )}
            {user ? (
              <button onClick={() => setShowForm(true)} className={product.demoUrl ? 'btn-secondary' : 'btn-primary'}>
                {t('Submit a lead')}
              </button>
            ) : (
              <Link to="/signup" className={product.demoUrl ? 'btn-secondary' : 'btn-primary'}>
                {t('Join to submit leads')}
              </Link>
            )}
          </div>
        </div>
      </header>

      <div className="grid gap-8 lg:grid-cols-3">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-6">
          <Block title={t('Why this business needs it')}>
            <p className="text-ink-100 whitespace-pre-line">{t(product.whyNeed)}</p>
          </Block>

          <Block title={t('What it solves')}>
            <ul className="space-y-2">
              {product.whatSolves.map((pain) => (
                <li key={pain} className="flex gap-3 text-ink-100">
                  <span className="shrink-0 text-red-400">✗</span>
                  <span>{t(pain)}</span>
                </li>
              ))}
            </ul>
          </Block>

          <Block title={t('Money impact')}>
            <p className="text-ink-100">{t(product.moneyImpact)}</p>
          </Block>

          <Block title={t("Why they'll pay")}>
            <p className="text-ink-100">{t(product.whyPay)}</p>
          </Block>

          <Block title={t('Features')}>
            <ul className="grid gap-2 sm:grid-cols-2">
              {product.features.map((f) => (
                <li key={f} className="flex gap-2 text-ink-100">
                  <span className="shrink-0 text-emerald-400">✓</span>
                  <span>{t(f)}</span>
                </li>
              ))}
            </ul>
          </Block>
        </div>

        {/* Sidebar: pricing + commission */}
        <aside className="space-y-4 lg:sticky lg:top-24 lg:self-start">
          <PlanCard
            name={t('Starter')}
            plan={product.plans.starter}
            comm={comm.starter}
          />
          <PlanCard
            name={t('Pro')}
            plan={product.plans.pro}
            comm={comm.pro}
            highlighted
          />
          <PlanCard
            name={t('Business')}
            plan={product.plans.business}
            comm={comm.business}
            addon
          />
          <div className="card text-xs text-ink-500">
            <div className="font-semibold text-ink-100 mb-2">{t('How commission works')}</div>
            <p>
              €{COMMISSION.setupFee} {t('setup fee is paid by the business at signup —')} <span className="text-emerald-400">{t('100% goes to you')}</span>.<br />
              {t('Then')} {COMMISSION.recurringPercent}% {t('of their monthly payment for')} <span className="text-emerald-400">{COMMISSION.recurringMonths} {t('months')}</span>.
            </p>
          </div>
        </aside>
      </div>

      {showForm && (
        <LeadFormModal
          product={product}
          onClose={() => setShowForm(false)}
          onSuccess={() => {
            setShowForm(false)
            setSubmitted(true)
          }}
        />
      )}

      {submitted && (
        <div className="fixed inset-x-0 bottom-4 z-50 flex justify-center">
          <div className="bg-emerald-600 text-white px-6 py-3 rounded-xl shadow-lg font-medium">
            {t("✓ Lead submitted! You'll see it in your dashboard.")}
          </div>
        </div>
      )}
    </section>
  )
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card">
      <h2 className="text-lg font-semibold text-white mb-3">{title}</h2>
      {children}
    </div>
  )
}

function PlanCard({
  name,
  plan,
  comm,
  highlighted,
  addon,
}: {
  name: string
  plan: { price: number; includes: string[] }
  comm: ReturnType<typeof calcCommission>
  highlighted?: boolean
  addon?: boolean
}) {
  const { t } = useI18n()
  const d = calcDomainCommission()
  return (
    <div className={`card ${highlighted ? 'border-brand-500/50 bg-gradient-to-br from-brand-600/10 to-transparent' : ''}`}>
      <div className="flex items-baseline justify-between">
        <h3 className="font-semibold text-white">{name}</h3>
        {highlighted && <span className="chip bg-brand-500/20 text-brand-500 border border-brand-500/30">{t('Popular')}</span>}
      </div>
      <div className="my-2 text-3xl font-bold text-white">
        €{plan.price}
        <span className="text-sm font-normal text-ink-500">{t('/month')}</span>
      </div>
      <ul className="text-sm text-ink-100 space-y-1 mb-4">
        {plan.includes.map((inc) => (
          <li key={inc} className="flex gap-1.5">
            <span className="text-emerald-400">✓</span> {t(inc)}
          </li>
        ))}
      </ul>
      <div className="border-t border-ink-800 pt-3 text-xs text-ink-500">
        {t('You earn:')}
        <div className="text-emerald-400 font-semibold text-base mt-0.5">
          €{comm.setup} + €{comm.monthly} × 6 = €{comm.total6m}
        </div>
      </div>
      {addon && (
        <div className="mt-3 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3">
          <div className="flex items-start gap-2">
            <span className="text-amber-400">🌐</span>
            <div className="text-xs">
              <div className="text-ink-100 font-medium">{t('Optional: custom domain + branded setup')}</div>
              <div className="text-ink-500 mt-0.5">
                {t('One-time')} €{d.price} {t('add-on · client gets their own')} <span className="font-mono">yoursite.mk</span>
              </div>
              <div className="text-emerald-400 font-semibold mt-1.5">
                +€{d.referrer} {t('instant bonus to you')}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─────── Lead form modal ───────
function LeadFormModal({
  product,
  onClose,
  onSuccess,
}: {
  product: ReturnType<typeof getProduct>
  onClose: () => void
  onSuccess: () => void
}) {
  const { t } = useI18n()
  const [form, setForm] = useState<LeadInput>({
    productSlug: product!.slug,
    plan: 'pro',
    businessName: '',
    ownerName: '',
    phone: '',
    email: '',
    website: '',
    city: '',
    country: '',
    notes: '',
    customIdea: product!.slug === 'custom' ? '' : undefined,
    budget: product!.slug === 'custom' ? undefined : undefined,
    customDomain: false,
  })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const isCustom = product!.slug === 'custom'

  function upd<K extends keyof LeadInput>(k: K, v: LeadInput[K]) {
    setForm((f) => ({ ...f, [k]: v }))
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setErr(null)
    try {
      await api.post('/api/leads', form)
      onSuccess()
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : t('Failed to submit lead'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto">
      <div className="w-full max-w-2xl card my-8">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-2xl font-bold text-white">{t('Submit lead —')} {product!.name}</h2>
          <button onClick={onClose} aria-label="Close" className="p-2 rounded-lg hover:bg-ink-800">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <Row>
            <Field label={t('Business name *')}>
              <input required className="input" value={form.businessName} onChange={(e) => upd('businessName', e.target.value)} />
            </Field>
            <Field label={t('Owner name *')}>
              <input required className="input" value={form.ownerName} onChange={(e) => upd('ownerName', e.target.value)} />
            </Field>
          </Row>

          <Row>
            <Field label={t('Phone *')}>
              <input required type="tel" className="input" value={form.phone} onChange={(e) => upd('phone', e.target.value)} />
            </Field>
            <Field label={t('Email *')}>
              <input required type="email" className="input" value={form.email} onChange={(e) => upd('email', e.target.value)} />
            </Field>
          </Row>

          <Row>
            <Field label={t('Business website')}>
              <input type="url" placeholder="https://…" className="input" value={form.website} onChange={(e) => upd('website', e.target.value)} />
            </Field>
            <Field label={t('City, Country *')}>
              <div className="grid grid-cols-2 gap-2">
                <input required placeholder={t('City')} className="input" value={form.city} onChange={(e) => upd('city', e.target.value)} />
                <input required placeholder={t('Country')} className="input" value={form.country} onChange={(e) => upd('country', e.target.value)} />
              </div>
            </Field>
          </Row>

          {!isCustom && (
            <>
              <Field label={t('Plan')}>
                <div className="grid grid-cols-3 gap-2">
                  {(['starter', 'pro', 'business'] as const).map((p) => (
                    <button
                      type="button"
                      key={p}
                      onClick={() => upd('plan', p)}
                      className={`rounded-xl border px-3 py-2 text-sm font-medium capitalize ${
                        form.plan === p ? 'bg-brand-600 border-brand-600 text-white' : 'bg-ink-900 border-ink-800 text-ink-100'
                      }`}
                    >
                      {p} · €{product!.plans[p].price}
                    </button>
                  ))}
                </div>
              </Field>
              {form.plan === 'business' && (
                <label className="flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.customDomain ?? false}
                    onChange={(e) => upd('customDomain', e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-amber-500"
                  />
                  <span className="text-sm">
                    <span className="text-ink-100 font-medium">{t('🌐 Add custom domain + branded setup (+€149 one-time)')}</span>
                    <span className="block text-xs text-ink-500 mt-0.5">
                      {t('Business gets their own')} <span className="font-mono">yoursite.mk</span>{t('. You earn +€44.70 instantly.')}
                    </span>
                  </span>
                </label>
              )}
            </>
          )}

          {isCustom && (
            <>
              <Field label={t('Describe what they need *')}>
                <textarea
                  required
                  rows={4}
                  className="input"
                  placeholder={t('What does this business do? What do they need built? What is their current process?')}
                  value={form.customIdea}
                  onChange={(e) => upd('customIdea', e.target.value)}
                />
              </Field>
              <Field label={t('Rough budget (€)')}>
                <input
                  type="number"
                  min={0}
                  className="input"
                  value={form.budget ?? ''}
                  onChange={(e) => upd('budget', e.target.value ? Number(e.target.value) : undefined)}
                />
              </Field>
            </>
          )}

          <Field label={t('Notes (optional)')}>
            <textarea rows={2} className="input" value={form.notes} onChange={(e) => upd('notes', e.target.value)} />
          </Field>

          {err && (
            <div className="rounded-xl border border-red-500/30 bg-red-500/10 text-red-400 text-sm p-3">{err}</div>
          )}

          <div className="flex gap-2 justify-end pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">{t('Cancel')}</button>
            <button type="submit" disabled={busy} className="btn-primary">
              {busy ? t('Submitting…') : t('Submit lead')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function Row({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-4 sm:grid-cols-2">{children}</div>
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="label">{label}</span>
      {children}
    </label>
  )
}
